import React, { useState, useCallback, useEffect } from "react";
import _ from "lodash";
import { AddOutlined, MinusCircleOutlined } from "@toddle-edu/ds-icons";
import {
  setValue,
  MAX_REMINDER_COUNT,
  getPresetReminders,
} from "../modules/Utils";
import { DEFAULT_REMINDER_VALUE_MAPPING } from "MyCalendar/modules/MyCalendarModule";
import { useI18n } from "Hooks";

export const useReminderList = props => {
  const {
    eventModalV2,
    updateEventModalV2,
    scrollToBottom,
    ...remProps
  } = props;
  const { t } = useI18n(["calendar", "common"]);
  const setReminder = setValue("reminders", updateEventModalV2);

  const [preferenceDropdownState, setPreferenceDropdownState] = useState(
    eventModalV2?.reminders
  );

  const addReminder = useCallback(
    value => {
      setReminder(value);
    },
    [setReminder]
  );

  useEffect(() => {
    if (scrollToBottom) {
      scrollToBottom("smooth");
    }
  }, [preferenceDropdownState]);

  useEffect(() => {
    setPreferenceDropdownState(eventModalV2?.reminders);
  }, [eventModalV2?.reminders]);

  /**
   * Finds the first available schedule time that hasn't been selected yet
   * @param {Array} existingReminders - Array of existing reminder objects
   * @returns {number} - Available schedule time in minutes
   */
  const findAvailableScheduleTime = existingReminders => {
    const presetOptions = getPresetReminders(t, eventModalV2?.isAllDayEvent);
    const selectedTimes = _.map(
      existingReminders,
      reminder => reminder?.scheduleTime?.value || reminder?.scheduleTime
    ).filter(time => time && time !== "custom");

    // Find the first preset option that hasn't been selected
    const availableOption = _.find(
      presetOptions,
      option =>
        option.value !== "custom" && !_.includes(selectedTimes, option.value)
    );

    // Return conditional default based on event type
    if (availableOption) {
      return availableOption.value;
    }

    // For all-day events, return 1 day (1440 minutes) as default
    // For regular events, use the existing default (10 minutes)
    return eventModalV2?.isAllDayEvent
      ? 1440 // 1 day in minutes
      : DEFAULT_REMINDER_VALUE_MAPPING?.scheduleTime;
  };

  const onAddReminder = () => {
    setPreferenceDropdownState(prev => {
      const availableScheduleTime = findAvailableScheduleTime(prev);
      const remindersList = [
        ...prev,
        {
          ...DEFAULT_REMINDER_VALUE_MAPPING,
          scheduleTime: availableScheduleTime,
          triggerDayOffset: availableScheduleTime / 60, // in Hours
          triggerTimeOffset: "09:00", // string in HH:mm
          id: Date.now(),
        },
      ];
      _.debounce(addReminder, 100)(remindersList);
      return remindersList;
    });
  };
  const getIcon = reminderIndex => {
    const reminderCount = _.size(preferenceDropdownState);
    const isLastReminder = reminderIndex === reminderCount - 1;
    if (MAX_REMINDER_COUNT - 1 === reminderIndex) {
      return <MinusCircleOutlined />;
    }
    return (reminderCount > 1 && isLastReminder) || reminderCount == 1 ? (
      <AddOutlined />
    ) : (
      <MinusCircleOutlined />
    );
  };

  const isAddFunction = reminderIndex => {
    const reminderCount = _.size(preferenceDropdownState);
    const isLastReminder = reminderIndex === reminderCount - 1;
    if (MAX_REMINDER_COUNT - 1 === reminderIndex) {
      return false;
    }
    return (reminderCount > 1 && isLastReminder) || reminderCount == 1
      ? true
      : false;
  };
  return {
    getIcon,
    isAddFunction,
    preferenceDropdownState,
    setPreferenceDropdownState,
    onAddReminder,
    initialValue: DEFAULT_REMINDER_VALUE_MAPPING,
    setReminder,
    eventModalV2,
    ...remProps,
  };
};
